/**
 * DriveAttachmentPicker — модалка выбора файла из доступных дисков.
 * UX: список дисков (drive-топиков) → клик → drill в папки → клик на файл → onPick.
 *
 * Реюзит /api/chat/drive/* (fetchDriveList, etc.) — те же endpoints что DriveView.
 * Возвращает DriveFile через onPick, родитель добавляет его в pendingFiles.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { Icon } from "@/components/prototype/Icon";
import { fetchDriveList, searchDrive, type DriveFile } from "@/api/drive";
import { type ChatTopic } from "@/api/dashboard";

interface GlobalSearchItem {
  kind: "folder" | "file";
  id: number;
  attachment_id?: number;
  name: string;
  drive_topic_id: number;
  drive_topic_name: string;
  path: string;
}

async function fetchGlobalSearch(q: string): Promise<GlobalSearchItem[]> {
  const data = await api<{ items: GlobalSearchItem[] }>(
    `/api/chat/drive/search-global?q=${encodeURIComponent(q)}`,
  );
  return data.items;
}

interface Props {
  /** Все доступные юзеру топики (фронт фильтрует по kind='drive'). */
  topics: ChatTopic[];
  onPick: (file: DriveFile) => void;
  onClose: () => void;
  /** Inline: рендер без overlay backdrop, поверх композера. */
  inline?: boolean;
}

export function DriveAttachmentPicker({ topics, onPick, onClose, inline = false }: Props) {
  const [topicId, setTopicId] = useState<number | null>(null);
  const [folderId, setFolderId] = useState<number | null>(null);
  const [searchQ, setSearchQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ.trim()), 200);
    return () => clearTimeout(t);
  }, [searchQ]);

  const searchMode = debouncedQ.length >= 2;

  const driveTopics = useMemo(
    () => topics.filter(t => t.kind === "drive"),
    [topics],
  );

  // Поиск (в зависимости от того, выбран ли drive)
  const globalSearchQ = useQuery({
    queryKey: ["drive-picker-global-search", debouncedQ],
    queryFn: () => fetchGlobalSearch(debouncedQ),
    enabled: searchMode && topicId == null,
    staleTime: 15_000,
  });
  const perTopicSearchQ = useQuery({
    queryKey: ["drive-picker-topic-search", topicId, debouncedQ],
    queryFn: () => searchDrive(topicId as number, { q: debouncedQ }),
    enabled: searchMode && topicId != null,
    staleTime: 15_000,
  });

  // Auto-select first if только один
  useEffect(() => {
    if (driveTopics.length === 1 && topicId == null) setTopicId(driveTopics[0].id);
  }, [driveTopics, topicId]);

  const listQ = useQuery({
    queryKey: ["drive/list", topicId, folderId],
    queryFn: () => fetchDriveList(topicId as number, folderId),
    enabled: topicId != null,
    staleTime: 30_000,
  });

  // ESC закрывает
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const goBackToTopics = useCallback(() => { setTopicId(null); setFolderId(null); }, []);

  const inner = (
      <div className={`drive-picker-modal${inline ? " drive-picker-inline" : ""}`} onMouseDown={(e) => inline && e.stopPropagation()} onClick={(e) => !inline && e.stopPropagation()}>
        <div className="drive-picker-header">
          {topicId != null && (
            <button
              type="button"
              className="drive-picker-back"
              onClick={() => {
                if (folderId != null && listQ.data?.breadcrumbs) {
                  const bc = listQ.data.breadcrumbs;
                  if (bc.length > 1) {
                    setFolderId(bc[bc.length - 2].id);
                  } else {
                    setFolderId(null);
                  }
                } else if (driveTopics.length > 1) {
                  goBackToTopics();
                }
              }}
              title="Назад"
            >
              <Icon name="chevron_l" size={20}/>
            </button>
          )}
          <div className="drive-picker-title">
            {topicId == null ? "Выберите диск" : (
              <span>
                {(driveTopics.find(t => t.id === topicId)?.name) ?? "Диск"}
                {listQ.data?.breadcrumbs.map(b => (
                  <span key={b.id}> / <span style={{ fontWeight: 500 }}>{b.name}</span></span>
                ))}
              </span>
            )}
          </div>
          <button type="button" className="drive-picker-close" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" size={18}/>
          </button>
        </div>

        <div className="drive-picker-search-wrap">
          <Icon name="search" size={14}/>
          <input
            type="search"
            className="drive-picker-search"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder={topicId == null
              ? "Поиск по всем дискам…"
              : "Поиск в этом диске…"}
          />
          {searchQ && (
            <button
              type="button"
              className="drive-picker-search-clear"
              onClick={() => setSearchQ("")}
              aria-label="Очистить"
            >
              <Icon name="x" size={12}/>
            </button>
          )}
        </div>

        <div className="drive-picker-body">
          {searchMode ? (
            // Search mode
            (() => {
              if (topicId == null) {
                // Global search
                const items = globalSearchQ.data ?? [];
                if (globalSearchQ.isLoading) return <div className="drive-picker-loading">Поиск…</div>;
                if (globalSearchQ.error) return (
                  <div className="drive-picker-error" role="alert">
                    <div>Не удалось выполнить поиск: {(globalSearchQ.error as Error).message}</div>
                    <button type="button" onClick={() => void globalSearchQ.refetch()}>
                      <Icon name="refresh" size={14}/> Повторить
                    </button>
                  </div>
                );
                if (items.length === 0) return <div className="drive-picker-empty">Ничего не найдено</div>;
                return (
                  <div className="drive-picker-content">
                    {items.map(it => (
                      <button
                        key={`${it.kind}-${it.id}`}
                        type="button"
                        className={`drive-picker-row${it.kind === "file" ? " drive-picker-row-file" : ""}`}
                        onClick={() => {
                          if (it.kind === "folder") {
                            setTopicId(it.drive_topic_id);
                            setFolderId(it.id);
                            setSearchQ("");
                          } else {
                            // Эмулируем DriveFile (минимум полей)
                            onPick({
                              id: it.id,
                              topic_id: it.drive_topic_id,
                              folder_id: null,
                              attachment_id: it.attachment_id ?? 0,
                              name: it.name,
                              uploaded_by_user_id: "",
                              uploaded_at: "",
                              mime: null,
                              size_bytes: 0,
                              width: null, height: null,
                              duration_sec: null, thumb_b64: null,
                            } as DriveFile);
                          }
                        }}
                      >
                        <span className="drive-picker-row-icon" style={{ color: it.kind === "folder" ? "#f5b942" : undefined }}>
                          <Icon name={it.kind === "folder" ? "folder" : "file"} size={22}/>
                        </span>
                        <span className="drive-picker-row-text">
                          <span className="drive-picker-row-name">{it.name}</span>
                          <span className="drive-picker-row-path">{it.path}</span>
                        </span>
                        {it.kind === "folder" && <span className="drive-picker-row-arrow">›</span>}
                      </button>
                    ))}
                  </div>
                );
              }
              // Per-topic search
              const sd = perTopicSearchQ.data;
              if (perTopicSearchQ.isLoading) return <div className="drive-picker-loading">Поиск…</div>;
              if (perTopicSearchQ.error) return (
                <div className="drive-picker-error" role="alert">
                  <div>Не удалось выполнить поиск: {(perTopicSearchQ.error as Error).message}</div>
                  <button type="button" onClick={() => void perTopicSearchQ.refetch()}>
                    <Icon name="refresh" size={14}/> Повторить
                  </button>
                </div>
              );
              const folders = sd?.folders ?? [];
              const files = sd?.files ?? [];
              if (folders.length === 0 && files.length === 0) {
                return <div className="drive-picker-empty">Ничего не найдено</div>;
              }
              return (
                <div className="drive-picker-content">
                  {folders.map(f => (
                    <button
                      key={`sf-${f.id}`}
                      type="button"
                      className="drive-picker-row"
                      onClick={() => { setFolderId(f.id); setSearchQ(""); }}
                    >
                      <span className="drive-picker-row-icon" style={{ color: "#f5b942" }}>
                        <Icon name="folder" size={22}/>
                      </span>
                      <span className="drive-picker-row-text">
                        <span className="drive-picker-row-name">{f.name}</span>
                        {f.path && <span className="drive-picker-row-path">{f.path}</span>}
                      </span>
                      <span className="drive-picker-row-arrow">›</span>
                    </button>
                  ))}
                  {files.map(f => (
                    <button
                      key={`sfile-${f.id}`}
                      type="button"
                      className="drive-picker-row drive-picker-row-file"
                      onClick={() => onPick({
                        id: f.id,
                        topic_id: topicId as number,
                        folder_id: f.folder_id,
                        attachment_id: f.attachment_id,
                        name: f.name,
                        uploaded_by_user_id: "",
                        uploaded_at: f.uploaded_at,
                        mime: f.mime, size_bytes: f.size_bytes,
                        width: f.width, height: f.height,
                        duration_sec: f.duration_sec, thumb_b64: f.thumb_b64,
                      } as DriveFile)}
                    >
                      <span className="drive-picker-row-icon"><Icon name="file" size={22}/></span>
                      <span className="drive-picker-row-text">
                        <span className="drive-picker-row-name">{f.name}</span>
                        {f.folder_path && <span className="drive-picker-row-path">{f.folder_path}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })()
          ) : topicId == null ? (
            <div className="drive-picker-topics">
              {driveTopics.length === 0 && (
                <div className="drive-picker-empty">Нет доступных дисков</div>
              )}
              {driveTopics.map(t => (
                <button
                  key={t.id}
                  type="button"
                  className="drive-picker-row"
                  onClick={() => setTopicId(t.id)}
                >
                  <span className="drive-picker-row-icon"><Icon name="folder" size={24}/></span>
                  <span className="drive-picker-row-name">{t.name}</span>
                  <span className="drive-picker-row-arrow">›</span>
                </button>
              ))}
            </div>
          ) : listQ.isLoading ? (
            <div className="drive-picker-loading">Загрузка…</div>
          ) : listQ.error ? (
            <div className="drive-picker-error" role="alert">
              <div>Не удалось загрузить Диск: {(listQ.error as Error).message}</div>
              <button type="button" onClick={() => void listQ.refetch()}>
                <Icon name="refresh" size={14}/> Повторить
              </button>
            </div>
          ) : (
            <div className="drive-picker-content">
              {(listQ.data?.folders ?? []).length === 0 && (listQ.data?.files ?? []).length === 0 && (
                <div className="drive-picker-empty">Пусто</div>
              )}
              {listQ.data?.folders.map(f => (
                <button
                  key={`fld-${f.id}`}
                  type="button"
                  className="drive-picker-row"
                  onClick={() => setFolderId(f.id)}
                >
                  <span className="drive-picker-row-icon" style={{ color: "#f5b942" }}>
                    <Icon name="folder" size={24}/>
                  </span>
                  <span className="drive-picker-row-name">{f.name}</span>
                  <span className="drive-picker-row-meta">{f.items_count} эл.</span>
                  <span className="drive-picker-row-arrow">›</span>
                </button>
              ))}
              {listQ.data?.files.map(f => (
                <button
                  key={`fil-${f.id}`}
                  type="button"
                  className="drive-picker-row drive-picker-row-file"
                  onClick={() => onPick(f)}
                >
                  <span className="drive-picker-row-icon">
                    {f.thumb_b64 ? (
                      <span
                        style={{
                          display: "inline-block", width: 32, height: 32, borderRadius: 4,
                          backgroundImage: `url(data:image/jpeg;base64,${f.thumb_b64})`,
                          backgroundSize: "cover", backgroundPosition: "center",
                        }}
                      />
                    ) : (
                      <Icon name="file" size={24}/>
                    )}
                  </span>
                  <span className="drive-picker-row-name">{f.name}</span>
                  <span className="drive-picker-row-meta">{fmtBytes(f.size_bytes)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
  );
  if (inline) return inner;
  return (
    <div className="drive-picker-bg" onClick={onClose}>
      {inner}
    </div>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} КБ`;
  return `${(n / 1024 / 1024).toFixed(1)} МБ`;
}
